import type { Account } from '../../core/accounts';
import type { Category } from '../../core/categories';
import { activeChips, clearChip, hasActiveFilters, type Filters } from '../../core/filters';
import { formatPaise } from '../../core/money';
import { TRANSACTION_TYPES, type TransactionType } from '../../core/transactions';

interface Props {
  filters: Filters;
  accounts: Account[];
  categories: Category[];
  tags: { id: string; name: string }[];
  onChange: (filters: Filters) => void;
}

/**
 * WD-06 — one filter bar, shared by the transaction list, reports and bulk reclassify.
 *
 * Built once on purpose. A second filter UI is how two screens start disagreeing about
 * what "this month, business only" means, and FR-RPT-01 requires one engine behind all
 * of them.
 */
export function FilterBar({ filters, accounts, categories, tags, onChange }: Props) {
  const set = <K extends keyof Filters>(key: K, value: Filters[K]) => {
    if (value === '' || value === undefined) onChange(clearChip(filters, key));
    else onChange({ ...filters, [key]: value });
  };

  const nameOf = (list: { id: string; name: string }[]) => (id: string) =>
    list.find((x) => x.id === id)?.name ?? id;

  const chips = activeChips(filters, {
    account: nameOf(accounts),
    category: nameOf(categories),
    tag: nameOf(tags),
    money: (paise) => formatPaise(paise, { compact: true }),
  });

  return (
    <div className="filter-bar">
      <div className="row">
        <select
          value={filters.accountId ?? ''}
          onChange={(e) => set('accountId', e.target.value || undefined)}
          aria-label="Account"
        >
          <option value="">All accounts</option>
          {accounts.filter((a) => !a.archived).map((a) => (
            <option key={a.id} value={a.id}>{a.name}</option>
          ))}
        </select>

        <select
          value={filters.type ?? ''}
          onChange={(e) => set('type', (e.target.value || undefined) as TransactionType | undefined)}
          aria-label="Type"
        >
          <option value="">All types</option>
          {TRANSACTION_TYPES.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>

        <select
          value={filters.categoryId ?? ''}
          onChange={(e) => set('categoryId', e.target.value || undefined)}
          aria-label="Category"
        >
          <option value="">All categories</option>
          {categories.filter((c) => !c.archived && c.parentId === null).map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>

        <select
          value={filters.isBusiness === undefined ? '' : String(filters.isBusiness)}
          onChange={(e) =>
            set('isBusiness', e.target.value === '' ? undefined : e.target.value === 'true')
          }
          aria-label="Business or personal"
        >
          <option value="">Business & personal</option>
          <option value="true">Business only</option>
          <option value="false">Personal only</option>
        </select>

        {tags.length > 0 && (
          <select
            value={filters.tagId ?? ''}
            onChange={(e) => set('tagId', e.target.value || undefined)}
            aria-label="Tag"
          >
            <option value="">All tags</option>
            {tags.map((t) => (
              <option key={t.id} value={t.id}>#{t.name}</option>
            ))}
          </select>
        )}

        {/* FR-TXN-20: the follow-up worklist. Imported rows with an issue and
            quick-captured entries carry the flag, and this is how they are found again. */}
        <select
          value={filters.needsEnrichment === undefined ? '' : String(filters.needsEnrichment)}
          onChange={(e) =>
            set('needsEnrichment', e.target.value === '' ? undefined : e.target.value === 'true')
          }
          aria-label="Follow-up"
        >
          <option value="">Any follow-up state</option>
          <option value="true">Needs follow-up</option>
          <option value="false">Already reviewed</option>
        </select>

        <input
          type="search"
          value={filters.search ?? ''}
          onChange={(e) => set('search', e.target.value || undefined)}
          placeholder="Search description, payee, notes…"
          aria-label="Search"
        />
      </div>

      {/* Active filters as removable chips, so what is applied is visible without
          opening anything — and an export that "looks wrong" is explained by the
          screen rather than by opening the file. */}
      {hasActiveFilters(filters) && (
        <div className="row chip-row">
          {chips.map((chip) => (
            <button
              key={chip.key}
              type="button"
              className="chip removable"
              onClick={() => onChange(clearChip(filters, chip.key))}
              aria-label={`Remove filter ${chip.label}`}
            >
              {chip.label} <span aria-hidden="true">✕</span>
            </button>
          ))}
          <button
            type="button"
            className="btn-link"
            onClick={() => onChange({ period: filters.period })}
          >
            Clear all
          </button>
        </div>
      )}
    </div>
  );
}
